/* © CNES */

import {
  CLOUD_COVER,
  DATASET,
  END_DATE_TIME,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT,
  INSTRUMENT_MODE,
  NODATA_PIXEL,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  PLATFORM,
  PPARAMETER,
  PROCESSING_LEVEL,
  PRODUCT_ACQUISITION_TYPE,
  PRODUCT_TYPE,
  START_DATE_TIME,
  VISIBILITY,
  WATER_COVER,
} from '../../../components/shared/Constants';

/**
 * Missions.
 */
export const Missions = {
  sentinel1: 2,
  sentinel2: 3,
  venus: 4,
  spot: 5,
  landsat: 6,
  multisat: 7,
  OSO: 8,
  postel: 9,
  GEOV2_AVHRR: 10,
  spirit: 11,
  trishna: 12,
  CO3D: 13
};

export const RefResultsSatelliteName = {
  S1: 'Sentinel 1',
  S2: 'Sentinel 2',
  SPOT: 'Spot',
  VM: 'Venus',
  SPOT4_TAKE5: 'Spot',
  SPOT5_TAKE5: 'Spot',
  LANDSAT: 'Landsat',
  GES: 'Spot',
  MULTISAT: 'Multisat',
  OSO: 'OSO',
  POSTEL: 'Postel',
  GEOV2_AVHRR: 'GEOV2 AVHRR',
  SPIRIT: 'Spirit',
  TRISHNA: 'TRISHNA',
  CO3D: 'CO3D'
};

export type SatelliteNameType =
  | 'S1'
  | 'S2'
  | 'SPOT'
  | 'VM'
  | 'SPOT4_TAKE5'
  | 'SPOT5_TAKE5'
  | 'LANDSAT'
  | 'GES'
  | 'MULTISAT'
  | 'OSO'
  | 'POSTEL'
  | 'GEOV2_AVHRR'
  | 'SPIRIT'
  | 'TRISHNA'
  | 'CO3D'
  | 'S1TILING';

/**
 * Product Types Names.
 */
export const productTypesNames = {
  S2MSI1C: 'Level 1C',
  S2MSI2A: 'Level 2A',
  SLC: 'Level 1 SLC',
  GRD: 'Level 1 GRD',
  OCN: 'Level 2 OCN',
  GRD_S1Tiling: 'Level 1 S1Tiling',
  REFLECTANCE: 'REFLECTANCE',
  REFLECTANCETOA: 'REFLECTANCETOA',
  DEM: 'DEM',
  DEM9V20: 'DEM9V20',
  DEMS9V20: 'DEMS9V20',
  DEMS09V20: 'DEMS09V20',
  DEMSV20: 'DEMSV20',
  DEPS9V20: 'DEPS9V20',
  SPOTDEM: 'SPOTDEM',
  SNOW_MASK: 'SNOW_MASK',
  L1C: 'L1C',
  L1A: 'L1A',
  L2A: 'L2A',
  L2C: 'L2C',
  L3A: 'L3A',
  'L2B-SNOW': 'L2B-SNOW',
  'L2B-WATER': 'L2B-WATER',
  'L3B-OSO': 'L3B-OSO',
  OSO: 'OSO',
  L3B: 'L3B-SNOW',
  L2B_BIOPHY: 'L2B_BIOPHY',
  Vegetation: 'Vegetation',
  Radiation: 'Radiation',
  Water: 'Water',
  LandCover: 'LandCover',
  L3: 'L3',
  L4: 'L4',
  L2B: 'L2B',
  IMREF: 'IMREF',
};
/**
 * Platforms referentiel by mission.
 */
export const refPlatforms = [
  { key: 'S1A', name: 'S1A', value: 2, missionId: [Missions.sentinel1] },
  { key: 'S1B', name: 'S1B', value: 3, missionId: [Missions.sentinel1] },
  { key: 'S2A', name: 'S2A', value: 4, missionId: [Missions.sentinel2] },
  { key: 'S2B', name: 'S2B', value: 5, missionId: [Missions.sentinel2] },
  { key: 'S2X', name: 'S2X', value: 6, missionId: [Missions.sentinel2] },
  { key: 'SPOT1', name: 'SPOT 1', value: 7, missionId: [Missions.spot] },
  { key: 'SPOT2', name: 'SPOT 2', value: 8, missionId: [Missions.spot] },
  { key: 'SPOT3', name: 'SPOT 3', value: 9, missionId: [Missions.spot] },
  { key: 'SPOT4', name: 'SPOT 4', value: 10, missionId: [Missions.spot] },
  {
    key: 'SPOT5',
    name: 'SPOT 5',
    value: 11,
    missionId: [Missions.spot, Missions.spirit],
  },
  {
    key: 'LANDSAT5',
    name: 'LANDSAT5',
    value: 12,
    missionId: [Missions.landsat],
  },
  {
    key: 'LANDSAT7',
    name: 'LANDSAT7',
    value: 13,
    missionId: [Missions.landsat],
  },
  {
    key: 'LANDSAT8',
    name: 'LANDSAT8',
    value: 14,
    missionId: [Missions.landsat],
  },
  { key: 'VM1', name: 'VM1', value: 15, missionId: [Missions.venus] },
  { key: 'VM5', name: 'VM5', value: 16, missionId: [Missions.venus] },
  {
    key: 'SPOT4_TAKE5',
    name: 'SPOT4_TAKE5',
    value: 17,
    missionId: [Missions.spot],
  },
  {
    key: 'SPOT5_TAKE5',
    name: 'SPOT5_TAKE5',
    value: 18,
    missionId: [Missions.spot],
  },
  { key: 'S1C', name: 'S1C', value: 19, missionId: [Missions.sentinel1] },
  { key: 'S2C', name: 'S2C', value: 20, missionId: [Missions.sentinel2] },
  { key: 'CO3D1', name: 'CO3D1', value: 22, missionId: [Missions.CO3D] },
  { key: 'CO3D2', name: 'CO3D2', value: 23, missionId: [Missions.CO3D] },
  { key: 'CO3D3', name: 'CO3D3', value: 24, missionId: [Missions.CO3D] },
  { key: 'CO3D4', name: 'CO3D4', value: 25, missionId: [Missions.CO3D] },
  { key: 'CO3DX', name: 'CO3DX', value: 26, missionId: [Missions.CO3D] },
  { key: 'S1D', name: 'S1D', value: 27, missionId: [Missions.sentinel1] },
];
/**
 * Missions referentiel by data type (PEPS_S1_L1).
 */
export const refMissions = [
  {
    key: [
      'PEPS_S1_L1',
      'PEPS_S1_L2',
      'THEIA_S1TILING_SENTINEL1_L1'
    ],
    name: 'Sentinel 1',
    value: Missions.sentinel1,
  },
  {
    key: [
      'PEPS_S2_L1C',
      'THEIA_REFLECTANCE_SENTINEL2_L2A',
      'THEIA_SNOW_SENTINEL2_L2B',
      'THEIA_WATERQUAL_SENTINEL2_L2B',
      'THEIA_REFLECTANCE_SENTINEL2_L3A',
      'THEIA_BIOPHY_SENTINEL2_L2B',
    ],
    name: 'Sentinel 2',
    value: Missions.sentinel2,
  },
  {
    key: [
      'THEIA_VENUS_VM1_L1C',
      'THEIA_VENUS_VM5_L1C',
      'THEIA_REFLECTANCE_VENUS_VM1_L2A',
      'THEIA_REFLECTANCE_VENUS_VM5_L2A',
      'THEIA_REFLECTANCE_VENUS_VM1_L3A',
      'THEIA_REFLECTANCE_VENUS_VM5_L3A',
    ],
    name: 'Venus',
    value: Missions.venus,
  },
  {
    key: [
      'THEIA_SWH-PHASE1_SPOT1_L1C',
      'THEIA_SWH-PHASE1_SPOT2_L1C',
      'THEIA_SWH-PHASE1_SPOT3_L1C',
      'THEIA_SPOT4_TAKE5_L1C',
      'THEIA_SWH-PHASE1_SPOT4_L1C',
      'THEIA_REFLECTANCE_SPOT4_TAKE5_L2A',
      'THEIA_REFLECTANCE_SPOT5_TAKE5_L2A',
      'THEIA_SPOT5_TAKE5_L1C',
      'THEIA_SWH-PHASE1_SPOT5_L1C',
      'SWH_SPOT123_L1',
      'SWH_SPOT4_L1',
      'SWH_SPOT5_L1',
    ],
    name: 'Spot',
    value: Missions.spot,
  },
  {
    key: [
      'THEIA_REFLECTANCE_LANDSAT5_L2A',
      'THEIA_REFLECTANCE_LANDSAT7_L2A',
      'THEIA_REFLECTANCE_LANDSAT8_L2A',
      'THEIA_SNOW_LANDSAT8_L2B',
    ],
    name: 'Landsat',
    value: Missions.landsat,
  },
  {
    key: ['THEIA_SNOW_MULTISAT_L3B'],
    name: 'Multisat',
    value: Missions.multisat,
  },
  {
    key: [
      'THEIA_OSO_RASTER_L3B',
      'THEIA_OSO_VECTOR_L3B',
    ],
    name: 'OSO',
    value: Missions.OSO,
  },
  {
    key: [
      'THEIA_POSTEL_VEGETATION_LAI',
      'THEIA_POSTEL_VEGETATION_FCOVER',
      'THEIA_POSTEL_VEGETATION_FAPAR',
      'THEIA_POSTEL_VEGETATION_NDVI',
      'THEIA_POSTEL_VEGETATION_SURFACEREFLECTANCE',
      'THEIA_POSTEL_RADIATION_BRDF',
      'THEIA_POSTEL_RADIATION_DLR',
      'THEIA_POSTEL_RADIATION_SURFACEREFLECTANCE',
      'THEIA_POSTEL_RADIATION_SURFACEALBEDO',
      'THEIA_POSTEL_WATER_SOILMOISTURE',
      'THEIA_POSTEL_WATER_SWI',
      'THEIA_POSTEL_WATER_SURFWET',
      'THEIA_POSTEL_WATER_PRECIP',
      'THEIA_POSTEL_LANDCOVER_GLOBCOVER',
    ],
    name: 'Postel',
    value: Missions.postel,
  },
  {
    key: [
      'THEIA_POSTEL_VEGETATION_LAI',
      'THEIA_POSTEL_VEGETATION_FCOVER',
      'THEIA_POSTEL_VEGETATION_FAPAR',
      'THEIA_POSTEL_VEGETATION_NDVI',
    ],
    name: 'GEOV2 AVHRR',
    value: Missions.GEOV2_AVHRR,
  },
  {
    key: ['THEIA_SPIRIT_SPOT5_L1A'],
    name: 'Spirit',
    value: Missions.spirit,
  },
  {
    key: [
      'TRISHNA_L1C',
      'TRISHNA_L2A',
      'TRISHNA_L2B',
      'TRISHNA_L3',
      'TRISHNA_DEM',
      'TRISHNA_IMREF',
    ],
    name: 'TRISHNA',
    value: Missions.trishna,
  },
  {
    key: [
      'CO3D_L2A',
      'CO3D_L2C',
      'CO3D_L3',
      'CO3D_L4',
    ],
    name: 'CO3D',
    value: Missions.CO3D,
  }
];

export const refDataProduct = [
  {
    key: 'SLC',
    name: 'SLC',
    value: 2,
    missionId: [Missions.sentinel1],
    type: 'productType',
    relation: ['L1'],
    color: 'blue',
  },
  {
    key: 'GRD',
    name: 'GRD',
    value: 3,
    missionId: [Missions.sentinel1],
    type: 'productType',
    relation: ['L1'],
    color: 'blue',
  },
  {
    key: 'GRD_S1Tiling',
    name: 'S1Tiling',
    value: 7,
    missionId: [Missions.sentinel1],
    type: 'productType',
    relation: ['L1'],
    color: 'blue',
  },
  {
    key: 'OCN',
    name: 'OCN',
    value: 4,
    missionId: [Missions.sentinel1],
    type: 'productType',
    relation: ['L2'],
    color: 'green',
  },
  {
    key: 'L1C',
    name: 'L1C',
    value: 5,
    missionId: [
      Missions.sentinel2,
      Missions.spot,
      Missions.venus,
      Missions.trishna,
    ],
    type: 'productLevel',
    relation: ['REFLECTANCE', 'REFLECTANCETOA', 'S2MSI1C', 'Multispectral'],
    color: 'blue',
  },
  {
    key: 'L2A',
    name: 'L2A',
    value: 9,
    missionId: [
      Missions.landsat,
      Missions.sentinel2,
      Missions.venus,
      Missions.spot,
      Missions.trishna,
    ],
    type: 'productLevel',
    relation: ['REFLECTANCE', 'Multispectral'],
    color: 'green',
  },
  {
    key: 'L2B-SNOW',
    name: 'L2B SNOW',
    value: 10,
    missionId: [Missions.landsat, Missions.sentinel2],
    type: 'productLevel',
    relation: ['SNOW_MASK'],
    color: 'green',
  },
  {
    key: 'L2B-WATER',
    name: 'L2B WATER',
    value: 13,
    missionId: [Missions.sentinel2],
    type: 'productLevel',
    relation: ['REFLECTANCE'],
    color: 'green',
  },
  {
    key: 'L3A',
    name: 'L3A',
    value: 14,
    missionId: [Missions.sentinel2, Missions.venus],
    type: 'productLevel',
    relation: ['REFLECTANCE'],
    color: 'green',
  },
  {
    key: 'L1A',
    name: 'L1A',
    value: 16,
    missionId: [Missions.spot, Missions.spirit],
    type: 'productLevel',
    relation: [
      'DEM',
      'REFLECTANCETOA',
      'DEM9V20',
      'DEMS9V20',
      'DEMS09V20',
      'DEMSV20',
      'DEPS9V20',
      'SPOTDEM',
    ],
    color: 'blue',
  },
  {
    key: 'L3B',
    name: 'L3B SNOW',
    value: 18,
    missionId: [Missions.multisat],
    type: 'productLevel',
    relation: ['SNOW_MASK'],
    color: 'green',
  },
  {
    key: 'L3B-OSO',
    name: 'L3B-OSO',
    value: 19,
    missionId: [Missions.OSO],
    type: 'productLevel',
    relation: ['OSO'],
    color: 'green',
  },
  {
    key: 'L2B_BIOPHY',
    name: 'L2B BIOPHY',
    value: 20,
    missionId: [Missions.sentinel2],
    type: 'productType',
    relation: ['L2B'],
    color: 'green',
  },
  {
    key: 'L2B',
    name: 'L2B',
    value: 21,
    missionId: [Missions.trishna],
    type: 'productLevel',
    relation: ['Multispectral'],
    color: 'green',
  },
  {
    key: 'L3',
    name: 'L3',
    value: 22,
    missionId: [Missions.trishna],
    type: 'productLevel',
    relation: ['Multispectral'],
    color: 'green',
  },
  {
    key: 'DEM',
    name: 'DEM',
    value: 23,
    missionId: [Missions.trishna],
    type: 'productType',
    relation: ['nodata'],
    color: 'green',
  },
  {
    key: 'IMREF',
    name: 'IMREF',
    value: 24,
    missionId: [Missions.trishna],
    type: 'productType',
    relation: ['nodata'],
    color: 'green',
  },
  {
    key: 'L2A',
    name: 'L2A',
    value: 25,
    missionId: [Missions.CO3D],
    type: 'productLevel',
    relation: ['REFLECTANCE'],
  },
  {
    key: 'L2C',
    name: 'L2C',
    value: 26,
    missionId: [Missions.CO3D],
    type: 'productLevel',
    relation: ['REFLECTANCE'],
  },
  {
    key: 'L3',
    name: 'L3',
    value: 27,
    missionId: [Missions.CO3D],
    type: 'productLevel',
    relation: ['DEM'],
  },
  {
    key: 'L4',
    name: 'L4',
    value: 28,
    missionId: [Missions.CO3D],
    type: 'productLevel',
    relation: ['DEM'],
  }
];

/**
 * Sensor Modes referential by Mission.
 */
export const refSensorModes = [
  { key: 'EW', name: 'EW', value: 2, missionId: Missions.sentinel1 },
  { key: 'IW', name: 'IW', value: 3, missionId: Missions.sentinel1 },
  { key: 'SM', name: 'SM', value: 4, missionId: Missions.sentinel1 },
  { key: 'WV', name: 'WV', value: 5, missionId: Missions.sentinel1 },
];

/**
 * Visibility Modes referential by Mission.
 */
export const refVisibility = [
  { key: 'PUBLIC', name: 'Public', value: 2, missionId: Missions.trishna },
  { key: 'EXPERT', name: 'Expert', value: 3, missionId: Missions.trishna },
  { key: 'PUBLIC', name: 'Public', value: 4, missionId: Missions.CO3D },
  { key: 'PRIVATE', name: 'Private', value: 5, missionId: Missions.CO3D },
];

/**
 * Acquisition Types referential by Mission.
 */
export const refAcquisitionType = [
  {
    key: 'DAY',
    name: 'DAY',
    value: 2,
    missionId: Missions.trishna,
  },
  {
    key: 'NIGHT',
    name: 'NIGHT',
    value: 3,
    missionId: Missions.trishna,
  },
  {
    key: 'Standard',
    name: 'Standard',
    value: 4,
    missionId: Missions.CO3D,
  },
  {
    key: 'Stereo',
    name: 'Stereo',
    value: 5,
    missionId: Missions.CO3D,
  }
];

/**
 * Filters to interrogate regards API on search items.
 */
export const filterSections = {
  // dateRange: { title: 'When', param: 'dateRange', op: 'in' },
  startDate: { title: 'When', param: START_DATE_TIME, op: 'lte' },
  endDate: { title: 'When', param: END_DATE_TIME, op: 'gte' },
  whereAbsoluteOrbit: {
    title: 'Where',
    param: ORBIT_ABSOLUTE,
    op: 'in',
  },
  whereRelativeOrbit: {
    title: 'Where',
    param: ORBIT_RELATIVE,
    op: 'in',
  },
  whereTile: {
    title: 'Where',
    param: GRID_CODE,
    op: 'contains',
  },
  mission: { title: 'Collection', param: DATASET, op: 'in' },
  platform: {
    title: 'Platform',
    param: PLATFORM,
    op: 'in',
  },
  dataProduct: {
    title: 'Data / Product',
    param: 'dataProduct',
    op: 'in',
  },
  productType: {
    title: 'Type of product',
    param: PRODUCT_TYPE,
    op: 'in',
  },
  variable: {
    title: 'Variable',
    param: PPARAMETER,
    op: 'in',
  },
  productLevel: {
    title: 'Product Level',
    param: PROCESSING_LEVEL,
    op: 'in',
  },
  sensorMode: {
    title: 'Sensor mode',
    param: INSTRUMENT_MODE,
    op: 'in',
  },
  cloudCover: {
    title: 'Cloud cover',
    param: CLOUD_COVER,
    op: 'between',
  },
  waterCover: {
    title: 'Water cover',
    param: WATER_COVER,
    op: 'between',
  },
  noDataPixel: {
    title: 'No data pixels',
    param: NODATA_PIXEL,
    op: 'between',
  },
  productName: {
    title: 'Product name',
    param: IDENTIFIER,
    op: 'contains',
  },
  satelliteSensor: {
    title: 'Satellite sensor',
    param: INSTRUMENT,
    op: 'in',
  },
  visibility: {
    title: 'Mode',
    param: VISIBILITY,
    op: 'in',
  },
  acquisitionType: {
    title: 'Acquisition Type',
    param: PRODUCT_ACQUISITION_TYPE,
    op: 'in',
  },
};

export const refProcessesNamesForQuicklook = [
  'tci-s2st',
  'rgb-s2st',
  'swir-s2st',
  'irgb-s2st',
  'ndvi-s2st',
];

export const RefSatelliteNames = [
  'S1',
  'S2',
  'Spot',
  'Venus',
  'OSO',
  'Landsat',
  'Spot',
  'Multisat',
  'Postel',
  'Trishna',
  'CO3D'
];

export const refGeoserverLayers = [
  {
    collections: [Missions.spot],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.spot))
      .map((platform) => platform.key),
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.spot))
      .map((product) => product.key),
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_SWH-PHASE1_SPOT1_L1C',
      'THEIA_SWH-PHASE1_SPOT2_L1C',
      'THEIA_SWH-PHASE1_SPOT3_L1C',
      'THEIA_SPOT4_TAKE5_L1C',
      'THEIA_SWH-PHASE1_SPOT4_L1C',
      'THEIA_REFLECTANCE_SPOT4_TAKE5_L2A',
      'THEIA_REFLECTANCE_SPOT5_TAKE5_L2A',
      'THEIA_SPOT5_TAKE5_L1C',
      'THEIA_SWH-PHASE1_SPOT5_L1C',
      'SWH_SPOT123_L1',
      'SWH_SPOT4_L1',
      'SWH_SPOT5_L1',
    ],
    layer: 'histo:gdh_spot',
  },
  {
    collections: [Missions.spirit],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.spirit))
      .map((platform) => platform.key),
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.spirit))
      .map((product) => product.key),
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: ['THEIA_SPIRIT_SPOT5_L1A'],
    layer: 'histo:gdh_spotdem',
  },
  {
    collections: [Missions.landsat],
    platforms: ['LANDSAT5', 'LANDSAT7'],
    dataproducts: ['L2A'],
    instruments: [],
    dataTypes: [
      'THEIA_REFLECTANCE_LANDSAT5_L2A',
      'THEIA_REFLECTANCE_LANDSAT7_L2A',
    ],
    layer: 'histo:gdh_landsat57',
  },
  {
    collections: [Missions.landsat],
    platforms: ['LANDSAT8'],
    dataproducts: ['L2A'],
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_REFLECTANCE_LANDSAT8_L2A',
    ],
    layer: 'muscate:gdh_landsat8',
  },
  {
    collections: [Missions.landsat, Missions.multisat, Missions.sentinel2],
    platforms: ['LANDSAT8', 'S2A', 'S2B', 'S2X'],
    dataproducts: ['L2B-SNOW', 'L2B-WATER', 'L2B_BIOPHY', 'L3B-SNOW', 'L2B', 'L3B'],
    instruments: [],
    dataTypes: [
      'THEIA_SNOW_LANDSAT8_L2B',
      'THEIA_SNOW_SENTINEL2_L2B',
      'THEIA_WATERQUAL_SENTINEL2_L2B',
      'THEIA_SNOW_MULTISAT_L3B',
      'THEIA_BIOPHY_SENTINEL2_L2B',
    ],
    layer: 'muscate:gdh_l2bl3b',
  },
  {
    collections: [Missions.sentinel2],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.sentinel2))
      .map((platform) => platform.key),
    dataproducts: ['L1C'],
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: ['PEPS_S2_L1C'],
    layer: 'peps:gdh_s2_l1c',
  },
  {
    collections: [Missions.sentinel2],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.sentinel2))
      .map((platform) => platform.key),
    dataproducts: ['L2A', 'L3A'],
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_REFLECTANCE_SENTINEL2_L2A',
      'THEIA_REFLECTANCE_SENTINEL2_L3A',
    ],
    layer: 'muscate:gdh_s2_l2a3a',
  },
  {
    collections: ['PLEIADES'],
    platforms: [],
    dataproducts: [],
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'MUSCATE_PLEIADES_PLEIADES_PRIMARY',
      'MUSCATE_PLEIADES_PLEIADES_ORTHO',
    ],
    layer: 'muscate:gdh_pleiades',
  },
  {
    collections: [Missions.venus],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.venus))
      .map((platform) => platform.key),
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.venus))
      .map((product) => product.key),
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_VENUS_VM1_L1C',
      'THEIA_VENUS_VM5_L1C',
      'THEIA_REFLECTANCE_VENUS_VM1_L2A',
      'THEIA_REFLECTANCE_VENUS_VM5_L2A',
      'THEIA_REFLECTANCE_VENUS_VM1_L3A',
      'THEIA_REFLECTANCE_VENUS_VM5_L3A',
    ],
    layer: 'muscate:gdh_venus',
  },
  {
    collections: [Missions.sentinel1],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.sentinel1))
      .map((platform) => platform.key),
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.sentinel1))
      .map((product) => product.key),
    instruments: refSensorModes
      .filter((sensorMode) => sensorMode.missionId === Missions.sentinel1)
      .map((sensorMode) => sensorMode.key),
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'PEPS_S1_L1',
      'PEPS_S1_L2',
      'THEIA_S1TILING_SENTINEL1_L1'
    ],
    layer: 'peps:gdh_s1',
  },
  {
    collections: [Missions.OSO],
    platforms: [],
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.OSO))
      .map((product) => product.key),
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_OSO_RASTER_L3B',
      'THEIA_OSO_VECTOR_L3B',
    ],
    layer: 'muscate:gdh_oso',
  },
  {
    collections: [Missions.postel],
    platforms: [],
    dataproducts: [],
    instruments: [],
    visibility: [],
    acquisitiontype: [],
    dataTypes: [
      'THEIA_POSTEL_VEGETATION_LAI',
      'THEIA_POSTEL_VEGETATION_FCOVER',
      'THEIA_POSTEL_VEGETATION_FAPAR',
      'THEIA_POSTEL_VEGETATION_NDVI',
      'THEIA_POSTEL_VEGETATION_SURFACEREFLECTANCE',
      'THEIA_POSTEL_RADIATION_BRDF',
      'THEIA_POSTEL_RADIATION_DLR',
      'THEIA_POSTEL_RADIATION_SURFACEREFLECTANCE',
      'THEIA_POSTEL_RADIATION_SURFACEALBEDO',
      'THEIA_POSTEL_WATER_SOILMOISTURE',
      'THEIA_POSTEL_WATER_SWI',
      'THEIA_POSTEL_WATER_SURFWET',
      'THEIA_POSTEL_WATER_PRECIP',
      'THEIA_POSTEL_LANDCOVER_GLOBCOVER',
    ],
    layer: 'histo:gdh_postel',
  },
  {
    collections: [Missions.trishna],
    platforms: [],
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.trishna))
      .map((product) => product.key),
    instruments: [],
    visibility: ['PUBLIC', 'EXPERT'],
    acquisitiontype: ['DAY', 'NIGHT'],
    dataTypes: [
      'TRISHNA_L1C',
      'TRISHNA_L2A',
      'TRISHNA_L2B',
      'TRISHNA_L3',
      'TRISHNA_DEM',
      'TRISHNA_IMREF',
    ],
    layer: 'trishna:gdh_trishna',
  },
  {
    collections: [Missions.CO3D],
    platforms: refPlatforms
      .filter((platform) => platform.missionId.includes(Missions.CO3D))
      .map((platform) => platform.key),
    dataproducts: refDataProduct
      .filter((product) => product.missionId.includes(Missions.CO3D))
      .map((product) => product.key),
    instruments: [],
    visibility: refVisibility
      .filter((visibility) => visibility.missionId === Missions.CO3D)
      .map((visibility) => visibility.key),
    acquisitiontype: refAcquisitionType
      .filter((acquisitionType) => acquisitionType.missionId === Missions.CO3D)
      .map((acquisitionType) => acquisitionType.key),
    dataTypes: [
      'CO3D_L2A',
      'CO3D_L2C',
      'CO3D_L3',
      'CO3D_L4'
    ],
    layer: 'co3d:gdh_co3d',
  }
];
